import React, { useLayoutEffect, useRef } from "react";
import { Box, Grid, Typography } from "@mui/material";
import useInView from "../../../../hooks/ui_hooks/useInView";
// ✅ Import the dashboard image from assets
import dashboardGraphic from "../../../../assets/home-dashboard-graphic.png";

export default function HowItWorks() {
  const [stepsRef, stepsEntered] = useInView({ threshold: 0.2 });
  const rowRefs = useRef([]);
  const fillRefs = useRef([]);

  // Drives the rail fill and the badge states straight from scroll position.
  // Writes to the DOM through refs rather than state — this runs per animation
  // frame while scrolling and must not re-render the tree.
  useLayoutEffect(() => {
    const container = stepsRef.current;
    if (!container) return;

    // Reduced motion keeps the static, fully-drawn timeline.
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return;

    container.setAttribute("data-timeline", "on");
    let frame = null;

    const update = () => {
      frame = null;

      // One rect first: skip the rest of the work when nowhere near the screen
      const box = container.getBoundingClientRect();
      if (box.bottom < -200 || box.top > window.innerHeight + 200) return;

      // Reading line ~62% down the viewport: the rail fills as each step
      // reaches roughly where the eye already is.
      const anchor = window.innerHeight * 0.62;

      fillRefs.current.forEach((fill) => {
        if (!fill) return;
        const r = fill.getBoundingClientRect();
        const ratio = r.height ? (anchor - r.top) / r.height : 0;
        fill.style.transform = `scaleY(${Math.min(1, Math.max(0, ratio))})`;
      });

      rowRefs.current.forEach((row) => {
        if (!row) return;
        // The badge is 40px tall at the top of each row
        const center = row.getBoundingClientRect().top + 20;
        row.dataset.active = center <= anchor ? "true" : "false";
      });
    };

    const onScroll = () => {
      if (frame === null) frame = requestAnimationFrame(update);
    };

    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);

    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (frame !== null) cancelAnimationFrame(frame);
      container.removeAttribute("data-timeline");
    };
  }, [stepsRef]);

  const steps = [
    {
      number: "1",
      status: "Sent",
      label: "We Send Privacy Requests For You",
      description:
        "We contact data brokers on your behalf and request removal of your exposed personal information.",
    },
    {
      number: "2",
      status: "In progress",
      label: "We Track Every Request",
      description:
        "You get real-time updates as each request moves through the removal process – from sent to completed.",
    },
    {
      number: "3",
      status: "Deleted",
      label: "We Show You What Requests Have Been Completed",
      description:
        "Once your request is complete, your dashboard updates instantly so you always know what's been cleaned.",
    },
  ];

  return (
    <Box component="section" sx={{ width: "100%", backgroundColor: "white" }}>
      <Box
        sx={{
          maxWidth: 1200,
          mx: "auto",
          px: { xs: 2, sm: 3, md: 4 },
          py: { xs: 4, md: 6 },
        }}
      >
        <Box sx={{ textAlign: "left", mb: { xs: 3, sm: 5 } }}>
          <Typography
            variant="h4"
            component="h2"
            sx={{
              fontWeight: 900,
              lineHeight: 1.25,
              color: "black",
            }}
          >
            How We Keep Your Information{" "}
            <Box component="span" sx={{ color: "#12b2f1" }}>
              Secure
            </Box>
          </Typography>
        </Box>

        <Box sx={{ position: "relative" }}>
          <Grid
            container
            spacing={6}
            alignItems="center"
            sx={{ position: "relative", zIndex: 1 }}
          >
            <Grid size={{ xs: 12, lg: 5 }}>
              <Box
                ref={stepsRef}
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 4,
                  // These pending styles only apply once the scroll effect has
                  // claimed the timeline, so without JS everything reads as
                  // fully active rather than permanently greyed out.
                  '&[data-timeline="on"] .hiw-badge': {
                    backgroundColor: "#e5e7eb",
                    color: "#9ca3af",
                  },
                  '&[data-timeline="on"] .hiw-status': { color: "#9ca3af" },
                  '&[data-timeline="on"] [data-active="true"] .hiw-badge': {
                    backgroundColor: "#12b2f1",
                    color: "white",
                  },
                  '&[data-timeline="on"] [data-active="true"] .hiw-status': {
                    color: "#12b2f1",
                  },
                }}
              >
                {steps.map(({ number, status, label, description }, i) => (
                  <Box
                    key={number}
                    ref={(el) => (rowRefs.current[i] = el)}
                    sx={{
                      position: "relative",
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 2.5,
                      opacity: stepsEntered ? 1 : 0,
                      transform: stepsEntered
                        ? "translateY(0)"
                        : "translateY(12px)",
                      transition: `opacity 0.6s ease ${0.15 + i * 0.18}s, transform 0.6s cubic-bezier(0.22,1,0.36,1) ${0.15 + i * 0.18}s`,
                      "@media (prefers-reduced-motion: reduce)": {
                        transition: "none",
                      },
                    }}
                  >
                    {/* Rail to the next step: a static track showing the path
                        ahead, with a fill that tracks scroll position. */}
                    {i < steps.length - 1 && (
                      <Box
                        aria-hidden
                        sx={{
                          position: "absolute",
                          left: "19px",
                          top: "44px",
                          bottom: "-32px",
                          width: "2px",
                          backgroundColor: "#e5e7eb",
                        }}
                      >
                        <Box
                          ref={(el) => (fillRefs.current[i] = el)}
                          sx={{
                            position: "absolute",
                            inset: 0,
                            backgroundColor: "#12b2f1",
                            transformOrigin: "top center",
                            // Filled by default; the scroll effect takes over
                            transform: "scaleY(1)",
                          }}
                        />
                      </Box>
                    )}

                    <Box
                      className="hiw-badge"
                      sx={{
                        position: "relative",
                        zIndex: 1,
                        width: 40,
                        height: 40,
                        borderRadius: "50%",
                        backgroundColor: "#12b2f1",
                        color: "white",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: 18,
                        fontWeight: 900,
                        flexShrink: 0,
                        // Punches a clean hole in the rail
                        boxShadow: "0 0 0 5px white",
                        transition:
                          "background-color 0.35s ease, color 0.35s ease",
                        "@media (prefers-reduced-motion: reduce)": {
                          transition: "none",
                        },
                      }}
                    >
                      {number}
                    </Box>

                    <Box>
                      {/* Mirrors the three stats in the dashboard shot */}
                      <Typography
                        className="hiw-status"
                        sx={{
                          fontSize: "0.62rem",
                          fontWeight: 800,
                          letterSpacing: "0.09em",
                          textTransform: "uppercase",
                          color: "#12b2f1",
                          mb: 0.5,
                          transition: "color 0.35s ease",
                          "@media (prefers-reduced-motion: reduce)": {
                            transition: "none",
                          },
                        }}
                      >
                        {status}
                      </Typography>
                      <Typography
                        variant="subtitle1"
                        sx={{ fontWeight: 800, mb: 0.5, lineHeight: 1.3 }}
                      >
                        {label}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: "#4b5563",
                          fontWeight: 450,
                          lineHeight: 1.6,
                        }}
                      >
                        {description}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            </Grid>

            <Grid size={{ xs: 12, lg: 7 }}>
              <Box
                sx={{
                  position: "relative",
                  borderRadius: 3,
                  overflow: "hidden",
                  boxShadow:
                    "0 24px 60px rgba(0,0,0,0.12), 0 4px 16px rgba(18,178,241,0.1)",
                  border: "1px solid rgba(18,178,241,0.15)",
                  transform: {
                    xs: "none",
                    lg: "perspective(1200px) rotateY(-3deg) rotateX(1deg)",
                  },
                  transition: "0.4s ease",
                  "&:hover": {
                    transform:
                      "perspective(1200px) rotateY(0deg) rotateX(0deg)",
                    boxShadow:
                      "0 32px 80px rgba(0,0,0,0.14), 0 8px 24px rgba(18,178,241,0.15)",
                  },
                }}
              >
                <Box
                  sx={{
                    bgcolor: "#f1f3f5",
                    px: 2,
                    py: 1,
                    display: "flex",
                    alignItems: "center",
                    gap: 0.75,
                    borderBottom: "1px solid rgba(0,0,0,0.08)",
                  }}
                >
                  <Box
                    sx={{
                      width: 10,
                      height: 10,
                      borderRadius: "50%",
                      bgcolor: "#ff5f57",
                    }}
                  />
                  <Box
                    sx={{
                      width: 10,
                      height: 10,
                      borderRadius: "50%",
                      bgcolor: "#febc2e",
                    }}
                  />
                  <Box
                    sx={{
                      width: 10,
                      height: 10,
                      borderRadius: "50%",
                      bgcolor: "#28c840",
                    }}
                  />
                  <Box
                    sx={{
                      ml: 1.5,
                      flex: 1,
                      bgcolor: "white",
                      borderRadius: 1,
                      px: 1.5,
                      py: 0.4,
                      border: "1px solid rgba(0,0,0,0.1)",
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: 10,
                        color: "#888",
                        fontFamily: "monospace",
                      }}
                    >
                      kloudscan.com/dashboard
                    </Typography>
                  </Box>
                </Box>

                {/* ✅ Updated to use the imported asset variable */}
                <Box
                  component="img"
                  src={dashboardGraphic}
                  alt="Kloudscan dashboard showing removal requests and data exposure"
                  sx={{
                    width: "100%",
                    display: "block",
                    objectFit: "cover",
                  }}
                />
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Box>
    </Box>
  );
}
